import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AdjustFundingModal } from '@/components/ltc-financial-planning/AdjustFundingModal';
import { InsufficientFundingBanner } from '@/components/ltc-financial-planning/InsufficientFundingBanner';
import { PolicyPreferenceCard } from '@/components/ltc-financial-planning/PolicyPreferenceCard';
import { PolicyQuoteCardsView } from '@/components/ltc-financial-planning/PolicyQuoteCardsView';
import { QuoteLoadingSequence } from '@client/components/ltc-financial-planning/QuoteLoadingSequence';
import { useLtcPlanningQuotes } from '@client/components/ltc-financial-planning/hooks/useLtcPlanningQuotes';
import { Button } from '@client/components/ui/button';
import { Card } from '@client/components/ui/card';
import { Skeleton } from '@client/components/ui/skeleton';
import type { Quote } from '@client/model';
import {
  selectClient,
  selectLtcPlanningQuotes,
  selectMaybeClientPartner,
  selectPolicyPreferences,
} from '@client/model/selectors';
import { groupQuotesByProduct } from '@client/utils/groupQuotesByProduct';
import { filterQuotesByPreferences } from '@client/utils/policyPreferenceFiltering';
import { useFundingSufficiency } from '../hooks/useFundingSufficiency';

import { isTraditionalLTCQuote } from '@/util/quoteTypeGuards';
import { cn } from '@client/lib/utils';
import { StepContentProps } from '../types';

function PoliciesStepHeader() {
  return (
    <div className="text-center">
      <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
        Highest Modeled ROI for Your Plan and Preferences
      </h1>
      <p className="mx-auto mt-3 max-w-2xl text-base text-gray-700">
        Here are the top-rated policy options based on your profile. Each option shows the highest
        return on investment for its product type.
      </p>
    </div>
  );
}

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="space-y-1">
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      {description && <p className="mt-1 text-sm text-gray-700">{description}</p>}
    </div>
  );
}

function PolicyQuoteCardsSkeleton() {
  return (
    <div className="mx-auto mt-12 flex w-full max-w-5xl flex-col gap-4">
      {[1, 2, 3].map(index => (
        <Card
          key={index}
          className="border-primary/20 w-full rounded-xl"
        >
          <div className="flex flex-col">
            {/* Header Row Skeleton */}
            <div className="flex items-start justify-between gap-8 p-6">
              {/* Product Info Section Skeleton */}
              <div className="flex items-start gap-3">
                <Skeleton className="size-16 shrink-0 rounded-lg" />
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>

              {/* Metrics Section Skeleton */}
              <div className="flex items-center gap-4">
                <div className="flex items-start gap-8">
                  {/* Projected Coverage Skeleton */}
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-7 w-28" />
                    <Skeleton className="mt-1 h-5 w-20 rounded-full" />
                  </div>

                  {/* Cost Skeleton */}
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-7 w-24" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                </div>

                {/* Expand Button Skeleton */}
                <Skeleton className="size-10 rounded-full" />
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200" />

            {/* Action Buttons Row Skeleton */}
            <div className="flex items-center justify-end gap-2 px-6 py-4">
              <Skeleton className="h-9 w-24 rounded-xl" />
              <Skeleton className="h-9 w-28 rounded-xl" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export function PoliciesStep({ navigation }: StepContentProps) {
  const navigate = useNavigate();
  const [isAdjustFundingModalOpen, setIsAdjustFundingModalOpen] = useState(false);
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const quotesData = useSelector(selectLtcPlanningQuotes);
  const policyPreferences = useSelector(selectPolicyPreferences);
  const [showLoadingSequence, setShowLoadingSequence] = useState(() => quotesData.length === 0);

  const { allQuotes, isLoading } = useLtcPlanningQuotes({
    enabled: true,
  });

  // Get funding sufficiency data
  const { policyPremium, totalExistingAssetsValue, isFunded, selectedPolicyNames } =
    useFundingSufficiency();

  const filteredQuotes = useMemo(() => {
    return filterQuotesByPreferences(allQuotes as Quote[], policyPreferences);
  }, [allQuotes, policyPreferences]);

  // Split allQuotes (unfiltered) for selected policies - they should bypass preference filtering
  const { allLifeInsuranceQuotes, allTraditionalLTCQuotes } = useMemo(() => {
    const lifeInsurance: Quote[] = [];
    const traditionalLTC: TraditionalLTCQuote[] = [];

    allQuotes.forEach(quote => {
      if (isTraditionalLTCQuote(quote)) {
        traditionalLTC.push(quote);
      } else {
        lifeInsurance.push(quote);
      }
    });

    return { allLifeInsuranceQuotes: lifeInsurance, allTraditionalLTCQuotes: traditionalLTC };
  }, [allQuotes]);

  // Split filteredQuotes for unselected policies - these respect preference filtering
  const { lifeInsuranceQuotes, traditionalLTCQuotes } = useMemo(() => {
    const lifeInsurance: Quote[] = [];
    const traditionalLTC: TraditionalLTCQuote[] = [];

    filteredQuotes.forEach(quote => {
      if (isTraditionalLTCQuote(quote)) {
        traditionalLTC.push(quote);
      } else {
        lifeInsurance.push(quote);
      }
    });

    return { lifeInsuranceQuotes: lifeInsurance, traditionalLTCQuotes: traditionalLTC };
  }, [filteredQuotes]);

  const uniqueProductCount = useMemo(() => {
    return groupQuotesByProduct(filteredQuotes).length;
  }, [filteredQuotes]);

  const hasPreferencesSet =
    policyPreferences.deathBenefit !== null ||
    policyPreferences.paymentStructure !== null ||
    policyPreferences.benefitStructure !== null;

  const hasAnyQuotes = allQuotes.length > 0;
  const hasFilteredQuotes = filteredQuotes.length > 0;

  if (showLoadingSequence) {
    return (
      <QuoteLoadingSequence
        isDataLoaded={!isLoading}
        onComplete={() => setShowLoadingSequence(false)}
      />
    );
  }

  if (!showLoadingSequence && !hasAnyQuotes && isLoading) {
    return (
      <div className="py-8">
        <PoliciesStepHeader />
        <PolicyQuoteCardsSkeleton />
      </div>
    );
  }

  // Show empty state if no quotes
  if (!hasAnyQuotes) {
    return (
      <div className="py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">No Quotes Available</h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-gray-700">
            We couldn't find any quotes matching your profile at this time. Please contact your
            advisor for assistance.
          </p>
        </div>
      </div>
    );
  }

  const handleAdjustFunding = () => {
    setIsAdjustFundingModalOpen(true);
  };

  return (
    <div className="space-y-8">
      {/* Fixed banner at the top */}
      {!isFunded && policyPremium > 0 && (
        <InsufficientFundingBanner
          policyPremium={policyPremium}
          totalExistingAssetsValue={totalExistingAssetsValue}
          onAdjustFunding={handleAdjustFunding}
        />
      )}

      <div className="space-y-6">
        <PoliciesStepHeader />
        <PolicyPreferenceCard
          className="mx-auto max-w-5xl"
          variant={!hasFilteredQuotes && hasPreferencesSet ? 'warning' : 'default'}
          warningMessage={
            !hasFilteredQuotes && hasPreferencesSet
              ? {
                  title: 'No matching policies found',
                  description:
                    'No policies match your current preferences. Showing all available options instead.',
                }
              : undefined
          }
        />
      </div>

      {/* Selected Policies Section */}
      {selectedPolicyNames.length > 0 && (
        <div className="mx-auto w-full max-w-5xl space-y-6">
          <SectionHeader title="In your Plan" />
          <PolicyQuoteCardsView
            lifeInsuranceQuotes={allLifeInsuranceQuotes}
            traditionalLTCQuotes={allTraditionalLTCQuotes}
            clientPartner={clientPartner}
            filterMode="selected"
            emptyMessage={{
              title: 'No selected policies',
              description: 'Add policies to your plan to see them here.',
            }}
          />
        </div>
      )}

      {!hasFilteredQuotes && hasPreferencesSet ? (
        <div className="mx-auto max-w-5xl space-y-6">
          {/* All Policies Section */}
          <div className="mx-auto w-full max-w-5xl space-y-6">
            <SectionHeader
              title="All Available Policies"
              description="Browse and compare all policy options below"
            />
            <PolicyQuoteCardsView
              lifeInsuranceQuotes={
                allQuotes.filter(q => !('traditionalLTCQuoteId' in q)) as Quote[]
              }
              traditionalLTCQuotes={allQuotes.filter(q => 'traditionalLTCQuoteId' in q) as any[]}
              clientPartner={clientPartner}
              filterMode="unselected"
            />
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* All Policies Section */}
          <div className="mx-auto w-full max-w-5xl space-y-6">
            <SectionHeader
              title="All Available Policies"
              description="Browse and compare all policy options below"
            />
            <PolicyQuoteCardsView
              lifeInsuranceQuotes={lifeInsuranceQuotes}
              traditionalLTCQuotes={traditionalLTCQuotes}
              clientPartner={clientPartner}
              filterMode="unselected"
            />
          </div>

          <div className="mx-auto max-w-5xl text-center text-sm text-gray-700">
            Showing {uniqueProductCount} unique {uniqueProductCount === 1 ? 'policy' : 'policies'}{' '}
            (highest ROI per product type)
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-4">
        <Button
          variant="secondary"
          className="rounded-xl px-6"
          onClick={() => navigate(`/clients/${client.clientId}/onboarding/shop-insurance`)}
        >
          Explore Alternative Quotes
        </Button>
      </div>

      <AdjustFundingModal
        open={isAdjustFundingModalOpen}
        onClose={() => setIsAdjustFundingModalOpen(false)}
        policyPremium={policyPremium}
        totalExistingAssetsValue={totalExistingAssetsValue}
        selectedPolicyNames={selectedPolicyNames}
      />
    </div>
  );
}
